import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Clock, RotateCcw, Trash2, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { RevisionDiffViewer } from './RevisionDiffViewer';
import { SanitizedContent } from '@/components/ui/sanitized-content';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface Revision {
  id: string;
  article_id: string;
  content: string;
  title: string;
  created_at: string;
  created_by: string;
  version_number: number;
}

interface ArticleRevisionHistoryProps {
  articleId?: string;
  onRestoreRevision: (content: string, title: string) => void;
}

export const ArticleRevisionHistory: React.FC<ArticleRevisionHistoryProps> = ({
  articleId,
  onRestoreRevision
}) => {
  const [revisions, setRevisions] = useState<Revision[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedRevision, setSelectedRevision] = useState<Revision | null>(null);
  const [compareWith, setCompareWith] = useState<Revision | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [revisionToDelete, setRevisionToDelete] = useState<string | null>(null);

  useEffect(() => {
    if (articleId) {
      fetchRevisions();
    }
  }, [articleId]);

  const fetchRevisions = async () => {
    if (!articleId) return;

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('article_revisions')
        .select('*')
        .eq('article_id', articleId)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      setRevisions(data || []);
    } catch (error) {
      console.error('Error fetching revisions:', error);
      toast.error('Failed to load revision history');
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = (revision: Revision) => {
    if (confirm(`Restore to version ${revision.version_number}? This will replace the current content.`)) {
      onRestoreRevision(revision.content, revision.title);
      toast.success(`Restored to version ${revision.version_number}`);
      setSelectedRevision(null);
    }
  };

  const handleDelete = async () => {
    if (!revisionToDelete) return;

    try {
      const { error } = await supabase
        .from('article_revisions')
        .delete()
        .eq('id', revisionToDelete);

      if (error) throw error;

      toast.success('Revision deleted');
      fetchRevisions();
    } catch (error) {
      console.error('Error deleting revision:', error);
      toast.error('Failed to delete revision');
    } finally {
      setDeleteDialogOpen(false);
      setRevisionToDelete(null);
    }
  };

  const toggleCompare = (revision: Revision) => {
    if (compareWith?.id === revision.id) {
      setCompareWith(null);
    } else {
      setCompareWith(revision);
    }
  };

  if (!articleId) {
    return (
      <Card>
        <CardContent className="p-6 text-center text-muted-foreground">
          Save the article first to track revision history
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Revision History
            </div>
            <Badge variant="outline">{revisions.length} versions</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[500px] pr-4">
            {loading ? (
              <div className="text-center p-4 text-muted-foreground">Loading...</div>
            ) : revisions.length === 0 ? (
              <div className="text-center p-4 text-muted-foreground">
                No revisions yet. Revisions are created automatically when you update the article.
              </div>
            ) : (
              <div className="space-y-2">
                {revisions.map((revision, index) => (
                  <div
                    key={revision.id}
                    className={`p-4 border rounded-lg transition-all ${
                      selectedRevision?.id === revision.id 
                        ? 'border-primary bg-primary/5' 
                        : 'hover:bg-muted/50'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant={index === 0 ? "default" : "outline"}>
                            Version {revision.version_number}
                          </Badge>
                          {index === 0 && (
                            <Badge variant="secondary" className="text-xs">Latest</Badge>
                          )}
                        </div>
                        <p className="text-sm font-medium truncate">{revision.title}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {new Date(revision.created_at).toLocaleString()}
                        </p>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSelectedRevision(
                            selectedRevision?.id === revision.id ? null : revision
                          )}
                          title="Toggle details"
                        >
                          {selectedRevision?.id === revision.id ? (
                            <EyeOff className="w-4 h-4" />
                          ) : (
                            <Eye className="w-4 h-4" />
                          )}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleRestore(revision)}
                          disabled={index === 0}
                          title="Restore this version"
                        >
                          <RotateCcw className="w-3 h-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setRevisionToDelete(revision.id);
                            setDeleteDialogOpen(true);
                          }}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          title="Delete revision"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>

                    {selectedRevision?.id === revision.id && (
                      <div className="mt-4 pt-4 border-t space-y-3">
                        <div className="prose dark:prose-invert max-w-none max-h-40 overflow-y-auto p-3 bg-muted rounded">
                          <SanitizedContent 
                            content={revision.content.substring(0, 500) + '...'}
                            className="text-sm"
                          />
                        </div>
                        {index < revisions.length - 1 && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => toggleCompare(revision)}
                            className="w-full"
                          >
                            {compareWith?.id === revision.id ? 'Hide' : 'Compare with previous version'}
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </CardContent>
      </Card>

      {compareWith && selectedRevision && revisions.indexOf(compareWith) < revisions.length - 1 && (
        <RevisionDiffViewer
          oldContent={revisions[revisions.indexOf(compareWith) + 1].content}
          newContent={compareWith.content}
          oldTitle={revisions[revisions.indexOf(compareWith) + 1].title}
          newTitle={compareWith.title}
        />
      )}

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Revision?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this revision. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
